import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UnitDTO } from "shared/types/unit";
import { fetchContractsCreatedThisYear } from "../api/contracts-this-year";

interface RentalMetrics {
  vacantUnits: number;
  vacantUnitsErv: number;
  upcomingVacantUnits: number;
  upcomingVacantUnitsErv: number;
  watchlistContracts: number;
  missingDocuments: number;
  outstandingInvoices: number;
  outstandingAmount: number;
  upcomingIndexations: number;
  totalRentalIncome: number;
  averageActualRent: number;
  occupancyRate: number;
  occupancyRateByUnits: number;
  occupancyRateByERV: number;
  valueCreation: {
    totalERV: number;
    totalActualRent: number;
    percentageDifference: number;
    unitComparisons: any[];
  };
  contractsCreatedThisYear: number;
  contractsCreatedThisYearValue: number;
  contractsCreatedThisYearDetails: any[];
}

export const useRentalMetrics = () => {
  const { session } = useSession();
  const [metrics, setMetrics] = useState<RentalMetrics>({
    vacantUnits: 0,
    vacantUnitsErv: 0,
    upcomingVacantUnits: 0,
    upcomingVacantUnitsErv: 0,
    watchlistContracts: 0,
    missingDocuments: 0,
    outstandingInvoices: 0,
    outstandingAmount: 0,
    upcomingIndexations: 0,
    totalRentalIncome: 0,
    averageActualRent: 0,
    occupancyRate: 0,
    occupancyRateByUnits: 0,
    occupancyRateByERV: 0,
    valueCreation: {
      totalERV: 0,
      totalActualRent: 0,
      percentageDifference: 0,
      unitComparisons: []
    },
    contractsCreatedThisYear: 0,
    contractsCreatedThisYearValue: 0,
    contractsCreatedThisYearDetails: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setIsLoading(true);
        // The expensive contracts-created-this-year query is fetched through a shared,
        // deduped helper so it is not hit multiple times across hooks/re-renders.
        const contractsThisYearPromise = fetchContractsCreatedThisYear(session);

        const [
          vacantResponse,
          upcomingVacantResponse,
          watchlistResponse,
          docsResponse,
          invoicesResponse,
          rentalStatsResponse,
          indexationResponse,
          upsellingResponse
        ] = await Promise.all([
          fetchWithAuth(`${API_URL}/rental/metrics/vacant-units`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/metrics/upcoming-vacant-units`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/watchlist`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/contracts/missing-documents`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/contracts/open-invoices`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/metrics/rental-statistics`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/contracts/near-indexation`, { method: "GET" }, session),
          fetchWithAuth(`${API_URL}/rental/metrics/upselling`, { method: "GET" }, session)
        ]);

        const [
          vacant,
          upcomingVacant,
          watchlist,
          docs,
          invoices,
          rentalStatsData,
          indexationData,
        ] = await Promise.all([
          vacantResponse.json(),
          upcomingVacantResponse.json(),
          watchlistResponse.json(),
          docsResponse.json(),
          invoicesResponse.json(),
          rentalStatsResponse.json(),
          indexationResponse.json(),
          upsellingResponse.json()
        ]);

        const contractsThisYearData = await contractsThisYearPromise;

        const outstandingAmount = Array.isArray(invoices)
          ? invoices.reduce((sum, inv) => sum + (inv.amount_residual || 0), 0)
          : 0;

        setMetrics({
          vacantUnits: Array.isArray(vacant) ? vacant.length : 0,
          vacantUnitsErv: Array.isArray(vacant) ? vacant.reduce((sum, unit: UnitDTO) => sum + (unit.estimatedRentalValue || 0), 0) : 0,
          upcomingVacantUnits: Array.isArray(upcomingVacant) ? upcomingVacant.length : 0,
          upcomingVacantUnitsErv: Array.isArray(upcomingVacant) ? upcomingVacant.reduce((sum, unit: UnitDTO) => sum + (unit.estimatedRentalValue || 0), 0) : 0,
          watchlistContracts: Array.isArray(watchlist) ? watchlist.length : 0,
          missingDocuments: Array.isArray(docs) ? docs.length : 0,
          outstandingInvoices: Array.isArray(invoices) ? invoices.length : 0,
          outstandingAmount,
          upcomingIndexations: Array.isArray(indexationData) ? indexationData.length : 0,
          totalRentalIncome: rentalStatsData?.totalRent || 0,
          averageActualRent: rentalStatsData?.residential?.averageActualRent || 0,
          occupancyRate: rentalStatsData?.occupancyRateByUnits || 0,
          occupancyRateByUnits: rentalStatsData?.occupancyRateByUnits || 0,
          occupancyRateByERV: rentalStatsData?.occupancyRateByERV || 0,
          valueCreation: rentalStatsData?.valueCreation || {
            totalERV: 0,
            totalActualRent: 0,
            percentageDifference: 0,
            unitComparisons: []
          },
          contractsCreatedThisYear: contractsThisYearData?.count || 0,
          contractsCreatedThisYearValue: contractsThisYearData?.totalValue || 0,
          contractsCreatedThisYearDetails: contractsThisYearData?.contractDetails || []
        });
      } catch (error) {
        console.error('Error fetching rental metrics:', error);
        setError('Failed to load metrics');
      } finally {
        setIsLoading(false);
      }
    };

    if (session) {
      fetchMetrics();
    }
    // Depend on the stable session id, not the session object reference,
    // to avoid re-fetching all metrics on every Clerk re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  return { metrics, isLoading, error, refetch: () => {} };
};
